import { FormikProps, useFormik } from 'formik';
import { IAccount, IAccountUpdatePayload } from 'shared/interfaces/IAccount';
import * as yup from 'yup';

type FormProps = {
  onSubmit: (values: IAccountUpdatePayload) => void;
  data: IAccount | undefined;
};

export const useBillingInformationForm = ({ onSubmit, data }: FormProps) => {
  const initialValues: IAccountUpdatePayload = {
    full_name: data && data.attributes.full_name ? data.attributes.full_name : '',
    first_name: data && data.attributes.first_name ? data.attributes.first_name : '',
    middle_name: data && data.attributes.middle_name ? data.attributes.middle_name : '',
    last_name: data && data.attributes.last_name ? data.attributes.last_name : '',
    stage_first_name: data && data.attributes.stage_first_name ? data.attributes.stage_first_name : '',
    stage_middle_name: data && data.attributes.stage_middle_name ? data.attributes.stage_middle_name : '',
    stage_last_name: data && data.attributes.stage_last_name ? data.attributes.stage_last_name : '',
    email: data && data.attributes.email ? data.attributes.email : '',
    contact_no: data && data.attributes.contact_no ? data.attributes.contact_no : '',
    country_code: data && data.attributes.country_code ? data.attributes.country_code : 'us',
    preferred_contact_method:
      data && data.attributes.preferred_contact_method ? data.attributes.preferred_contact_method : '',
    representation: data && data.attributes.representation ? data.attributes.representation : false,
    gender: data && data.attributes.gender ? data.attributes.gender : '',
    country: data && data.attributes.country ? data.attributes.country : '',
    state_region: data && data.attributes.state_region ? data.attributes.state_region : '',
    unions: data && data.attributes.unions ? data.attributes.unions : [],
    other_profession_list: data && data.attributes.other_profession_list ? data.attributes.other_profession_list : [],
    other_email_addresses: data && data.attributes.other_email_addresses ? data.attributes.other_email_addresses : [],
    other_phone_numbers: data && data.attributes.other_phone_numbers ? data.attributes.other_phone_numbers : [],
    emergency_contact: data && data.attributes.emergency_contact ? data.attributes.emergency_contact : null,
    notes: data && data.attributes.notes ? data.attributes.notes : '',
    billing_information: data && data.attributes.billing_information ? data.attributes.billing_information : null,
  };

  const validationScheme: yup.SchemaOf<IAccountUpdatePayload> = yup.object({
    first_name: yup.string().required('First name is required'),
    middle_name: yup.string().notRequired(),
    last_name: yup.string().required('Last name is required'),
    stage_first_name: yup.string().required('Stage first name is required'),
    stage_middle_name: yup.string().notRequired(),
    stage_last_name: yup.string().required('Stage last name is required'),
    email: yup.string().required('Email is required').email('Email is invalid'),
    contact_no: yup.string().notRequired(),
    country_code: yup.string().notRequired(),
    preferred_contact_method: yup.string().notRequired(),
    representation: yup.boolean().notRequired(),
    full_name: yup.string().notRequired(),
    gender: yup.string().notRequired(),
    country: yup.string().notRequired(),
    state_region: yup.string().notRequired(),
    primary_type: yup.string().notRequired(),
    adult_minor: yup.string().notRequired(),
    age_range_from: yup.string().notRequired(),
    age_range_to: yup.string().notRequired(),
    birth_date: yup.string().notRequired(),
    other_profession_list: yup.array().notRequired(),
    other_email_addresses: yup.array().notRequired(),
    other_phone_numbers: yup.array().notRequired(),
    emergency_contact: yup.object().nullable(),
    notes: yup.string().notRequired(),
    billing_information: yup.object().nullable(),
    unions: yup
      .array()
      .of(
        yup.object().shape(
          {
            name: yup.string().when('id_number', {
              is: (id_number: string) => id_number,
              then: yup.string().required('Union is required'),
              otherwise: yup.string().notRequired(),
            }),
            id_number: yup.string().when('name', {
              is: (name: string) => name,
              then: yup.string().required('ID Number is required'),
              otherwise: yup.string().notRequired(),
            }),
          },
          [['id_number', 'name']],
        ),
      )
      .min(0)
      .notRequired(),
    work_permit_expired_at: yup.string().notRequired(),
    with_minor_work_permit: yup.boolean().notRequired(),
    drivers_license_expiration_date: yup.string().notRequired(),
    passport_expiration_date: yup.string().notRequired(),
  });

  const form: FormikProps<IAccountUpdatePayload> = useFormik({
    initialValues,
    validationSchema: validationScheme,
    validateOnBlur: true,
    onSubmit: (values, helpers) => {
      onSubmit(values);
    },
    enableReinitialize: true,
  });

  return {
    form,
  };
};
